import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MEMBERS_PAGE_SIZE, type MemberActivity, type Role } from '@ready/shared';
import { useApi, type Member, type MemberSummary } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { Button, Card, ErrorText } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import {
  ACTIVITY_OPTIONS,
  ActivityBadge,
  Filter,
  Pager,
  lastActiveLabel,
  ofTotal,
} from '@/components/people';
import { useOrgContext } from './layout';

const SOURCE_LABELS: Record<string, string> = {
  creator: 'Created the organization',
  domain: 'Email domain',
  invitation: 'Invitation',
  invite_code: 'Invite code',
  csv: 'Bulk upload',
  join_request: 'Approved request',
  sso: 'Single sign-on',
};

/**
 * Members for the org's admins (and admins of orgs above it): search,
 * filters and pages, with an engagement snapshot per member and totals on
 * top. Engagement counts only what happened in this org's tree.
 */
export function OrgMembersPage() {
  const { org, usage, canManage, role } = useOrgContext();
  const isAdminView = canManage || role === null;
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [roleFilter, setRoleFilter] = useState<Role | ''>('');
  const [activity, setActivity] = useState<MemberActivity | ''>('');
  const [groupId, setGroupId] = useState('');
  const [sort, setSort] = useState<'name' | 'joined' | 'active'>('name');
  const [descendants, setDescendants] = useState(false);
  const [page, setPage] = useState(0);
  const hasChildren = Boolean(usage && usage.usage.maxChildOrgs > 0) && !org.parentId;

  const query = {
    q: q || undefined,
    role: roleFilter || undefined,
    activity: activity || undefined,
    sort,
    descendants: descendants || undefined,
    groupId: groupId || undefined,
    offset: page * MEMBERS_PAGE_SIZE,
    limit: MEMBERS_PAGE_SIZE,
  };
  const members = useQuery({
    queryKey: ['members', org.id, query],
    queryFn: () => api.members(org.id, query),
    enabled: isAdminView,
    placeholderData: keepPreviousData,
  });
  const groups = useQuery({
    queryKey: ['groups', org.id],
    queryFn: () => api.groups(org.id),
    enabled: isAdminView,
  });
  const summary = useQuery({
    queryKey: ['memberSummary', org.id, descendants],
    queryFn: () => api.memberSummary(org.id, descendants),
    enabled: isAdminView,
  });
  if (!isAdminView) return <Navigate to=".." replace />;
  const groupOptions = (groups.data?.groups ?? []).map(
    (g) => [g.id, `${g.name} (${g.members})`] as [string, string],
  );

  /** Any filter change starts again from the first page. */
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
  };
  const total = members.data?.total ?? 0;
  const filtered = Boolean(q || roleFilter || activity || groupId);

  return (
    <div className="space-y-6">
      <Summary data={summary.data} />

      <Card
        title="Members"
        action={
          <div className="flex flex-wrap items-center gap-4">
            {hasChildren && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input
                  type="checkbox"
                  className="size-5 accent-primary"
                  checked={descendants}
                  onChange={(e) =>
                    refilter(() => {
                      setDescendants(e.target.checked);
                      if (e.target.checked) setGroupId('');
                    })
                  }
                />
                Include child organizations
              </label>
            )}
            {canManage && (
              <Link to="../invites">
                <Button variant="secondary">Invite members</Button>
              </Link>
            )}
          </div>
        }
      >
        <div className="space-y-3">
          <SearchBox
            value={text}
            onChange={(v) => refilter(() => setText(v))}
            placeholder="Search by name or email"
            label="Search members"
          />
          <div
            className={`grid gap-3 sm:grid-cols-2 ${groupOptions.length ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}
          >
            {groupOptions.length > 0 && (
              <Filter
                id="m-group"
                label="Group"
                value={groupId}
                onChange={(v) =>
                  refilter(() => {
                    setGroupId(v);
                    // A group belongs to this org, so its members are listed here only.
                    if (v) setDescendants(false);
                  })
                }
                options={[['', 'All groups'], ...groupOptions]}
              />
            )}
            <Filter
              id="m-role"
              label="Role"
              value={roleFilter}
              onChange={(v) => refilter(() => setRoleFilter(v as Role | ''))}
              options={[
                ['', 'All roles'],
                ...(['owner', 'admin', 'author', 'learner'] as const).map(
                  (r) => [r, ROLE_LABELS[r]] as [string, string],
                ),
              ]}
            />
            <Filter
              id="m-activity"
              label="Activity"
              value={activity}
              onChange={(v) => refilter(() => setActivity(v as MemberActivity | ''))}
              options={[['', 'Everyone'], ...ACTIVITY_OPTIONS]}
            />
            <Filter
              id="m-sort"
              label="Sort by"
              value={sort}
              onChange={(v) => refilter(() => setSort(v as typeof sort))}
              options={[
                ['name', 'Name'],
                ['active', 'Most recently active'],
                ['joined', 'Newest members'],
              ]}
            />
          </div>
        </div>

        <div className="mt-5">
          {members.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : members.isError ? (
            <ErrorText error={members.error} />
          ) : members.data.members.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {filtered ? 'No members match that search or filters.' : 'No members yet.'}
            </p>
          ) : (
            <MemberRows members={members.data.members} showOrg={descendants} />
          )}
        </div>

        <Pager
          page={page}
          pageSize={MEMBERS_PAGE_SIZE}
          total={total}
          busy={members.isFetching}
          onPage={setPage}
        />
      </Card>
    </div>
  );
}

/** Totals for the org (or its tree): people by activity, and what they've completed here. */
function Summary({ data: s }: { data: MemberSummary | undefined }) {
  if (!s) return null;
  const pct = (n: number) => (s.members ? Math.round((n / s.members) * 100) : 0);
  const n = (v: number) => v.toLocaleString('en-US');
  const stats: [string, string, string?][] = [
    ['Members', n(s.members)],
    [
      'Active in 30 days',
      n(s.active),
      `${pct(s.active)}% · ${n(s.quiet)} quiet · ${n(s.inactive)} inactive`,
    ],
    ['Courses completed', n(s.coursesCompleted)],
    ['Certificates', n(s.certificates)],
    ['Assessments passed', n(s.quizzesPassed)],
    ['Events attended', n(s.eventsAttended)],
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
      {stats.map(([label, value, hint]) => (
        <Card key={label} className={hint ? 'col-span-2 lg:col-span-1' : undefined}>
          {/* Two lines for every label, so the numbers line up whether or not one wraps. */}
          <p className="min-h-[2lh] text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </Card>
      ))}
    </div>
  );
}

const lastActive = (m: Member) => lastActiveLabel(m.lastActiveAt);

function MemberRows({ members, showOrg }: { members: Member[]; showOrg: boolean }) {
  const name = (m: Member) => (
    <Link to={m.userId} className="-my-1 block py-1 hover:text-primary">
      <span className="block break-all font-medium">{m.displayName ?? m.email}</span>
      {m.displayName && (
        <span className="block break-all text-xs text-muted-foreground">{m.email}</span>
      )}
    </Link>
  );
  return (
    <>
      {/* Phones: one row per member, details underneath. */}
      <ul className="divide-y divide-border sm:hidden">
        {members.map((m) => (
          <li key={`${m.orgId}-${m.userId}`} className="space-y-1.5 py-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">{name(m)}</div>
              <ActivityBadge activity={m.activity} />
            </div>
            <p className="text-xs text-muted-foreground">
              {ROLE_LABELS[m.role]}
              {showOrg ? ` · ${m.orgName}` : ''}
              {m.groups.length ? ` · ${m.groups.join(', ')}` : ''} · Joined {formatDate(m.joinedAt)}{' '}
              · {lastActive(m)}
            </p>
            <p className="text-xs text-muted-foreground">
              Courses {ofTotal(m.coursesCompleted, m.coursesStarted)} · Assessments{' '}
              {ofTotal(m.quizzesPassed, m.quizzesTaken)} · Events {m.eventsAttended} · Certificates{' '}
              {m.certificates}
            </p>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2 pr-4 font-medium">Member</th>
              <th className="py-2 pr-4 font-medium">Role</th>
              <th className="py-2 pr-4 font-medium">Activity</th>
              <th className="py-2 pr-4 font-medium" title="Completed of started">
                Courses
              </th>
              <th className="py-2 pr-4 font-medium" title="Passed of taken">
                Assessments
              </th>
              <th className="py-2 pr-4 font-medium">Events</th>
              <th className="py-2 pr-4 font-medium">Certificates</th>
              <th className="py-2 font-medium">Joined</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {members.map((m) => (
              <tr key={`${m.orgId}-${m.userId}`} className="align-top">
                <td className="py-2.5 pr-4">
                  {name(m)}
                  {showOrg && (
                    <span className="block text-xs text-muted-foreground">{m.orgName}</span>
                  )}
                  {m.groups.length > 0 && (
                    <span className="block text-xs text-muted-foreground">
                      {m.groups.join(', ')}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-4">{ROLE_LABELS[m.role]}</td>
                <td className="py-2.5 pr-4">
                  <ActivityBadge activity={m.activity} />
                  <span className="mt-1 block text-xs text-muted-foreground">{lastActive(m)}</span>
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {ofTotal(m.coursesCompleted, m.coursesStarted)}
                </td>
                <td className="py-2.5 pr-4 tabular-nums">
                  {ofTotal(m.quizzesPassed, m.quizzesTaken)}
                </td>
                <td className="py-2.5 pr-4 tabular-nums">{m.eventsAttended || '—'}</td>
                <td className="py-2.5 pr-4 tabular-nums">{m.certificates || '—'}</td>
                <td className="py-2.5 text-muted-foreground">
                  {formatDate(m.joinedAt)}
                  <span className="block text-xs">{SOURCE_LABELS[m.source] ?? m.source}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
